import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

/**
 * Nível de acesso ao protótipo, derivado de qual código de acesso foi digitado.
 *
 * - `internal`: uso do time — sidebar expansível e navegável, comentários, "Sair" no avatar.
 * - `client`:   teste com usuário — shell travado, sem comentários (ver commit 1466d87).
 *
 * Isto NÃO é uma barreira de segurança: a SPA é estática e os dois códigos ficam legíveis
 * no bundle. É um seletor de modo.
 */
export type PrototypeRole = 'internal' | 'client';

const ROLE_KEY = 'barte-prototype-role';
// Chave da versão de código único, anterior aos dois papéis. Não é mais lida: ela não diz
// qual papel a pessoa teria, e assumir `client` prendia quem já estava desbloqueado — o
// papel cliente não tem "Sair". Só é limpada, para não deixar resíduo.
const LEGACY_UNLOCKED_KEY = 'barte-prototype-access-code-unlocked';

interface PrototypeRoleValue {
  role: PrototypeRole;
  logout: () => void;
  /**
   * Colapso da sidebar. Vive aqui, acima das `Routes`, e não no `useShellNav`: cada rota
   * monta um layout novo, então um estado local seria descartado a cada navegação — o
   * interno colapsava a sidebar, clicava num item de menu e ela voltava expandida.
   */
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
}

const Ctx = createContext<PrototypeRoleValue | null>(null);

export function usePrototypeRole(): PrototypeRoleValue {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('usePrototypeRole precisa estar dentro de <PrototypeRoleProvider>');
  return ctx;
}

/**
 * Papel persistido, ou `null` se ninguém desbloqueou o protótipo neste browser.
 *
 * `internalEnabled` é obrigatório de propósito: com default `true`, um chamador futuro que
 * esquecesse a opção reintroduziria silenciosamente o caminho privilegiado. Assim a
 * garantia fica com o compilador.
 *
 * Com `internalEnabled` falso — variável ausente ou os dois códigos iguais — um `internal`
 * persistido é rebaixado a `client`. Sem isso o fail-safe do gate valeria só no submit, e
 * quem desbloqueou como interno antes da configuração quebrar seguiria interno. O
 * rebaixamento NÃO é persistido, de propósito: corrigida a variável, o browser volta a ser
 * interno sozinho, e ninguém fica preso em `client` (que não tem "Sair").
 */
export function readStoredRole({ internalEnabled }: { internalEnabled: boolean }): PrototypeRole | null {
  try {
    const stored = localStorage.getItem(ROLE_KEY);
    if (stored === 'internal') return internalEnabled ? 'internal' : 'client';
    if (stored === 'client') return 'client';
  } catch (_) {
    /* localStorage indisponível */
  }
  return null;
}

export function storeRole(role: PrototypeRole) {
  try {
    localStorage.setItem(ROLE_KEY, role);
    localStorage.removeItem(LEGACY_UNLOCKED_KEY);
  } catch (_) {
    /* localStorage indisponível */
  }
}

export function clearStoredRole() {
  try {
    localStorage.removeItem(ROLE_KEY);
    localStorage.removeItem(LEGACY_UNLOCKED_KEY);
  } catch (_) {
    /* localStorage indisponível */
  }
}

interface PrototypeRoleProviderProps {
  role: PrototypeRole;
  onLogout: () => void;
  children: React.ReactNode;
}

export function PrototypeRoleProvider({ role, onLogout, children }: PrototypeRoleProviderProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const toggleSidebar = useCallback(() => setSidebarCollapsed((c) => !c), []);
  const value = useMemo(
    () => ({ role, logout: onLogout, sidebarCollapsed, toggleSidebar }),
    [role, onLogout, sidebarCollapsed, toggleSidebar]
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * Estado de papel do gate: o que está persistido hoje e como sair.
 * Fica aqui, e não no gate, para manter as chaves de storage num só arquivo.
 */
export function useRoleSession({ internalEnabled }: { internalEnabled: boolean }) {
  const [role, setRole] = useState<PrototypeRole | null>(() =>
    readStoredRole({ internalEnabled })
  );

  const unlock = useCallback((next: PrototypeRole) => {
    storeRole(next);
    setRole(next);
  }, []);

  const logout = useCallback(() => {
    clearStoredRole();
    setRole(null);
  }, []);

  return { role, unlock, logout };
}
